import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leadingIcon?: ReactNode;
  invalid?: boolean;
}

/**
 * Input — paper-calm background, hairline border, focus uses real outline.
 *
 * Per `component-api.md §9` accessibility:
 *   - 44×44 px tap-target
 *   - Real `outline` focus ring (not box-shadow)
 *   - Color not the only signal (label + icon required)
 *
 * W56.0 H (ADR-0200) — **the focus ring is drawn on the shell, not on the
 * inner `<input>`.**
 *
 * The shell is the visible field: it carries the border, the fill and the
 * padding; the `<input>` inside it is transparent and borderless. An outline
 * on the inner element therefore drew a 2px frame *around the text*, inset by
 * the shell's 12px padding — a smaller, floating rectangle inside the field
 * rather than a ring around it. On top of that the shell's
 * `focus-within:border-you-strong` changed the border colour at the same
 * time, so the field showed two indicators at once.
 *
 * Now: the shell draws one real outline (`focus-within:outline-*`), and the
 * inner element opts out (`focus:outline-none`) so the global
 * `:focus-visible` rule does not draw a second one on top.
 *
 * `focus:outline-none` on the inner element is Tailwind's **shorthand**
 * (`outline: 2px solid transparent`) — it suppresses the ring by making it
 * transparent, not by removing the property. That works because the global
 * rule now lives in `@layer base` and utilities beat it; before ADR-0200 the
 * global rule was unlayered and won, so this class was dead.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leadingIcon, invalid, className, type = "text", ...rest },
  ref,
) {
  return (
    <div
      className={cn(
        "tap-target flex items-center gap-2",
        "rounded-md bg-paper-calm border",
        invalid ? "border-partner" : "border-hairline",
        // One indicator, and it is a **real outline** — a `box-shadow` ring is
        // dropped entirely by Windows forced-colors mode, which would leave a
        // keyboard user with no focus indicator at all.
        "focus-within:outline focus-within:outline-2 focus-within:outline-you-strong focus-within:outline-offset-2",
        "transition-colors duration-quick ease-paper",
        "px-3",
        className,
      )}
    >
      {leadingIcon ? (
        <span className="shrink-0 text-ink-2" aria-hidden>
          {leadingIcon}
        </span>
      ) : null}
      <input
        ref={ref}
        type={type}
        data-invalid={invalid ? "true" : undefined}
        className={cn(
          "flex-1 bg-transparent focus:outline-none",
          "text-ink-1 placeholder:text-ink-3",
          "h-11",
        )}
        {...rest}
        // Deliberately **after** `{...rest}`.
        //
        // `invalid` is this component's contract, so it must win over a
        // hand-written `aria-invalid` in `rest`; putting the derived value
        // last makes that true by construction instead of by convention.
        // It also makes the false case *remove* the attribute
        // (`undefined` → React omits it) rather than leaving a stale
        // `aria-invalid="false"` behind — a screen reader must not be told
        // "this field is merely not-invalid" when the field simply has no
        // validation state at all.
        //
        // `aria-describedby` is unaffected: it is a different attribute and
        // still rides in on `rest`, so a caller can point the input at any
        // description it likes.
        aria-invalid={invalid ? "true" : undefined}
      />
    </div>
  );
});

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      data-invalid={invalid ? "true" : undefined}
      className={cn(
        "w-full rounded-md bg-paper-calm border",
        invalid ? "border-partner" : "border-hairline",
        // Same one-indicator rule as `Input`, except the textarea *is* the
        // visible box — so the outline goes on the element itself.
        //
        // `focus:` rather than `focus-visible:` because a textarea is only
        // ever focused in order to be typed into; a mouse click lands the
        // caret and the ring is the right response there. (`Input` uses
        // `focus-within:` for the same reason — its shell cannot receive
        // `:focus-visible` itself.)
        "focus:outline focus:outline-2 focus:outline-you-strong focus:outline-offset-2",
        "transition-colors duration-quick ease-paper",
        "px-3 py-3 text-body text-ink-1 placeholder:text-ink-3",
        "resize-y",
        className,
      )}
      {...rest}
      // Same ordering rule as `Input`: the derived `aria-invalid` goes last
      // so the `invalid` prop wins over a hand-written one, and so the
      // attribute is absent (not `"false"`) when there is no invalid state.
      aria-invalid={invalid ? "true" : undefined}
    />
  );
});
